/**
 * Вход в систему.
 *
 * Публичной регистрации нет: учётные записи заводит владелец. Сообщение об
 * ошибке нейтрально и не раскрывает, существует ли адрес.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { loginSchema } from '@project-os/validation';
import { ApiError } from '@/api/client';
import { useLogin, useSession } from '@/api/auth';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BrandMark } from '@/components/common/brand';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session, isPending } = useSession();
  const login = useLogin();

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', rememberMe: true },
  });

  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/projects';

  if (!isPending && session) {
    return <Navigate to={redirectTo} replace />;
  }

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      void navigate(redirectTo, { replace: true });
    } catch (error) {
      // Ошибки полей приходят от сервера в том же формате, что и локальные.
      if (error instanceof ApiError && error.fields) {
        for (const [field, message] of Object.entries(error.fields)) {
          form.setError(field as Parameters<typeof form.setError>[0], { message });
        }
      }
    }
  });

  const formError =
    login.error instanceof ApiError && !login.error.fields ? login.error : undefined;

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <div className="flex justify-end p-3">
        <ThemeToggle />
      </div>

      <div className="flex flex-1 items-center justify-center px-4 pb-24">
        <div className="w-full max-w-sm">
          <div className="mb-7 text-center">
            <BrandMark className="mx-auto mb-4 size-10" />
            <h1 className="text-xl font-medium tracking-tight">
              project <span className="font-semibold">OS</span>
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Внутренняя платформа состояния проектов
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              {formError ? (
                <Alert variant="destructive">
                  <AlertDescription>
                    {formError.message}
                    {formError.hint ? (
                      <span className="mt-1 block opacity-80">{formError.hint}</span>
                    ) : null}
                  </AlertDescription>
                </Alert>
              ) : null}

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Электронная почта</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="username"
                        autoFocus
                        placeholder="name@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Пароль</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={login.isPending}>
                {login.isPending ? <Loader2Icon className="animate-spin" /> : null}
                Войти
              </Button>
            </form>
          </Form>

          <p className="mt-6 text-center text-xs leading-relaxed text-muted-foreground">
            Регистрация закрыта. Учётные записи создаёт владелец системы и передаёт ссылку для
            установки пароля лично.
          </p>
        </div>
      </div>
    </div>
  );
}
